import trim from 'lodash/trim'
import qs from 'query-string'

import { getBillingApiUrl } from './Env'

export async function tryParse(response: Response): Promise<any> {
  const dataType = (response.headers.get('content-type') ?? '')
    .toLowerCase()
    .split(';')
    .map((text) => trim(text))

  if (dataType.includes('application/json')) {
    return response.json()
  }

  if (dataType.some((text) => text.startsWith('text/'))) {
    return response.text()
  }

  return response.body
}

export default function createRestClient(workspaceId?: string) {
  const createFetchInternal = (method: RequestInit['method']) => async <T>(
    relativeURL: string,
    options: Omit<RequestInit, 'method'> & {
      json?: unknown
      query?: Record<string, string | number | null | undefined>
    } = {},
  ): Promise<T> => {
    const fullURL = [
      getBillingApiUrl(),
      workspaceId ? `workspaces/${workspaceId}` : '',
      relativeURL,
    ]
      .map((part) => trim(part, '/'))
      .filter((part) => !!part)
      .join('/')

    const response = await fetch(
      qs.stringifyUrl(
        {
          url: fullURL,
          query: options.query,
        },
        {
          skipNull: true,
          skipEmptyString: true,
        },
      ),
      {
        credentials: 'include',
        ...options,
        method,
        headers: options.json
          ? {
              'Content-Type': 'application/json',
            }
          : {},
        body: options.json ? JSON.stringify(options.json) : undefined,
      },
    )

    if (response.ok) {
      return tryParse(response)
    }

    throw new RestClientError(response.status, await tryParse(response))
  }

  return {
    get: createFetchInternal('GET'),
    post: createFetchInternal('POST'),
    patch: createFetchInternal('PATCH'),
    delete: createFetchInternal('DELETE'),
  }
}

export class RestClientError extends Error {
  constructor(public readonly status: number, public readonly data: any) {
    super(String(status))

    if (typeof data?.statusCode === 'number') {
      this.status = data.statusCode
    }

    if (typeof data?.message === 'string') {
      this.message = data.message
    }
  }
}
